import { Easing, SharedValue, defineAnimation } from 'react-native-reanimated';

type AnimationPhase = 'in' | 'out' | 'beforeIn' | 'beforeOut' | 'exit';

type CustomAnimation = {
  phase: AnimationPhase;
  current: number;
  startTime: number;
  pausedTime: number;
  startValue: number;
  exitDuration: number;
  easing: (value: number) => number;
};

export type Props = {
  isVisible: SharedValue<boolean>;
  isPaused: SharedValue<boolean>;
  startPhase: AnimationPhase;
  minZoom: number;
  maxZoom: number;
  startZoom: number;
  duration: {
    in: number;
    out: number;
    beforeIn: number;
    beforeOut: number;
  };
  exitSpeed: number;
};

export type ZoomInOutAnimationModifierUtil = {
  toValue: number;
  current: number;
  onStart: (
    animation: CustomAnimation,
    value: number,
    now: number,
    previousAnimation: CustomAnimation,
  ) => void;
  onFrame: (animation: CustomAnimation, now: number) => true | false;
  progress: number;
  startTime: number;
  startValue: number;
  type: string;
  easing: (t: number) => number;
};

export const ZoomInOutAnimationModifier = (
  props: Props,
): ZoomInOutAnimationModifierUtil => {
  'worklet';

  return defineAnimation(props.startZoom, () => {
    'worklet';

    function onFrame(animation: CustomAnimation, now: number) {
      if (!props.isVisible.value && animation.phase !== 'exit') {
        if (Math.abs(animation.current - props.maxZoom) < 0.01) {
          animation.current = props.maxZoom;
          return true;
        }
        animation.startTime = now;
        animation.pausedTime = 0;
        animation.startValue = animation.current;
        animation.exitDuration =
          (Math.abs(animation.current - props.maxZoom) * 200) / props.exitSpeed;
        animation.phase = 'exit';
        return false;
      }

      if (animation.pausedTime === 0) {
        if (props.isPaused.value && props.isVisible.value) {
          animation.pausedTime = now;
        }
      } else {
        if (!props.isPaused.value || !props.isVisible.value) {
          animation.startTime =
            animation.startTime + (now - animation.pausedTime);
          animation.pausedTime = 0;
        }
      }

      const runtime = now - animation.startTime;

      if (animation.pausedTime !== 0) {
        return false;
      }

      if (animation.phase === 'out') {
        if (runtime >= props.duration.out) {
          animation.startTime = now;
          animation.current = props.minZoom;
          animation.phase = 'beforeIn';
          return false;
        }

        const progress = 1 - animation.easing(runtime / props.duration.in);
        animation.current =
          props.minZoom + (props.maxZoom - props.minZoom) * progress;
        return false;
      } else if (animation.phase === 'beforeIn') {
        animation.current = props.minZoom;
        if (runtime >= props.duration.beforeIn) {
          animation.startTime = now;
          animation.phase = 'in';
          return false;
        }
        return false;
      } else if (animation.phase === 'in') {
        if (runtime >= props.duration.in) {
          animation.startTime = now;
          animation.current = props.maxZoom;
          animation.phase = 'beforeOut';
          return false;
        }

        const progress = animation.easing(runtime / props.duration.in);
        animation.current =
          props.minZoom + (props.maxZoom - props.minZoom) * progress;
        return false;
      } else if (animation.phase === 'beforeOut') {
        animation.current = props.maxZoom;
        if (runtime >= props.duration.beforeOut) {
          animation.startTime = now;
          animation.phase = 'out';
          return false;
        }
        return false;
      } else if (animation.phase === 'exit') {
        if (runtime >= animation.exitDuration) {
          animation.startTime = now;
          animation.current = props.maxZoom;
          return true;
        }

        const progress = animation.easing(runtime / animation.exitDuration);
        animation.current =
          animation.startValue +
          (props.maxZoom - animation.startValue) * progress;
        return false;
      }
      return false;
    }

    function onStart(
      animation: CustomAnimation,
      value: number,
      now: number,
      previousAnimation: CustomAnimation,
    ) {
      animation.phase = props.startPhase;
      animation.startTime = now;
      animation.pausedTime = 0;
      animation.current = props.startZoom;

      if (previousAnimation && previousAnimation.phase !== 'exit') {
        animation.current = previousAnimation.current;
        animation.pausedTime = previousAnimation.pausedTime;
        animation.phase = previousAnimation.phase;
        animation.startTime = previousAnimation.startTime;
        animation.startValue = previousAnimation.startValue;
        animation.exitDuration = previousAnimation.exitDuration;
      }
    }

    return {
      type: 'timing',
      onFrame: onFrame,
      onStart: onStart,
      progress: 0,
      toValue: 1,
      startValue: props.startZoom,
      startTime: 0,
      easing: Easing.inOut(Easing.ease),
      current: 1,
    };
  });
};
//# sourceMappingURL=timing.js.map
